import React from 'react'
import './index.css'
import './init.js'
import SmartAccess from './SmartAccess'
import { configureAmplify } from './common/amplifyServices'
import { createRoot } from 'react-dom/client'

import amplifyConfigs from './config/account-config.json'
export default amplifyConfigs

// --- Helper Functions ---

/**
 * Extracts the dev parts from the query string.
 */
function getDevParts() {
  const devQuery = window.location.search.substr(1)
  return devQuery ? devQuery.split('/') : []
}

/**
 * Parses the initial identifier from the dev parts.
 * Supports /?id=VALUE or /?identifier=VALUE in segments after the first.
 */
function getDevInitialIdentifier(devParts) {
  for (let i = 1; i < devParts.length; i++) {
    const segment = devParts[i]
    if (!segment) continue

    if (segment.startsWith('?id=')) {
      return segment.substring(4)
    }
    if (segment.startsWith('?identifier=')) {
      return segment.substring('?identifier='.length)
    }
  }
  return undefined
}

/**
 * Resolves the default and alternative client names based on configuration and hostname.
 */
function resolveClientNames() {
  const validKeys = Object.keys(amplifyConfigs).filter(
    (key) =>
      typeof amplifyConfigs[key] === 'object' && amplifyConfigs[key] !== null
  )

  let defaultClient = validKeys.length === 1 ? validKeys[0] : undefined
  let altClient

  if (defaultClient && !window.location.hostname.includes(defaultClient)) {
    const match = window.location.hostname.match(/smart-([^.]+)\.co\.uk/)
    altClient = match ? match[1] : undefined

    if (altClient && amplifyConfigs[altClient] !== defaultClient) {
      defaultClient = undefined
    }
  }

  return { defaultClientName: defaultClient, altClientName: altClient }
}

function formatAccountName(name) {
  if (name === 'smartpear') {
    return 'Smart Pear'
  }
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function updateDocumentTitle(isDev, devClientName) {
  const acct = isDev
    ? devClientName
    : window.location.hostname.match(/(smart[^.]+)\.co\.uk$/)?.[1]

  if (acct) {
    document.title = formatAccountName(acct)
  }
}

// --- Main Execution ---

const devParts = getDevParts()
const devClientName = devParts[0] || ''
const devInitialIdentifier = getDevInitialIdentifier(devParts)

const { defaultClientName, altClientName } = resolveClientNames()

const isDev = import.meta.env.MODE === 'development'
const clientName =
  defaultClientName ||
  (isDev ? devClientName : window.location.hostname.split('.')[0])

const clientId =
  amplifyConfigs[clientName] && amplifyConfigs[clientName].bucketName

// Load custom theme
import(`./styles/${clientName}.css`)
  .then(() => console.log(`Theme loaded: ${clientName}`))
  .catch(() => console.error(`No custom theme for ${clientName}`))

updateDocumentTitle(isDev, devClientName)

if (clientId) {
  configureAmplify(clientName, amplifyConfigs, altClientName)
}

const container = document.getElementById('root')
const root = createRoot(container)
root.render(
  <SmartAccess
    clientName={clientName}
    altClientName={altClientName}
    clientId={clientId}
    initialIdentifier={devInitialIdentifier}
  />
)
